import { useCallback } from 'react';
import { Typography, Button, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	Application,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { InterviewSection } from './InterviewSection';
import { useInterviewLandingPage } from './useInterviewLandingPage';
import {
	ANSWERS_REVIEW_CONTEXT,
	APPLICANT_ADDITIONAL_DETAILS,
	APPLICANT_CONTEXT,
	APPROVER_RECOMMENDATION,
	CARE_TEAM_RECOMMENDATION,
	FAMILY_CONTEXT,
	FUNCTIONARY_HOME,
	OTHER_REFERENCE_CONTEXT,
	PRECEPTOR_CONTEXT,
	PROPOSER_RECOMMENDATION,
	SKIP_ALL_INTERVIEWS,
	VIEW_ANSWERS,
	ZC_RECOMMENDATION,
} from '../../../shared/Routes';
import isEmpty from 'lodash/isEmpty';
import { useBatchId } from '../../../hooks/useBatchId';
import { isProposerSkippedToEnd } from '../../../shared/Utils';
import { useApplicationStatusTranslation } from '../../../hooks/useApplicationStatusTranslation';
import { useAuth } from '../../../hooks/useAuth';
import { ApplicationStatus } from '../../../shared/Constants';
import { ApplicantDetailsTable } from '../ApplicantDetailsTable';
import Icon from '../../../components/ui-components/Icon';
import RightArrowCircle from '../../../assets/images/right-arrow-circle.svg';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import LeftArrow from '../../../assets/images/arrow-left.svg';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface LandingPageProps {
	application: Partial<Application>;
	onReviewAnswersClick: () => void;
	onInterviewCandidateClick: () => void;
	onInterviewPreceptorClick: () => void;
	onInterviewFamilyMemberClick: () => void;
	onInterviewOtherReferenceClick: () => void;
	onProposerRecommendationClick: () => void;
	onCareTeamRecommendationClick: () => void;
	onZCRecommendationClick: () => void;
	onApproverRecommendationClick: () => void;
	onAdditionalDetailsClick: () => void;
	onUnableToProceedClick: () => void;
	onBackClick: () => void;
	onViewAnswersClick: () => void;
}

const SectionHeader = ({
	heading,
	onBackClick,
	isMobile,
}: {
	heading: string;
	onBackClick: () => void;
	isMobile: boolean;
}) => {
	const { t } = useTranslation();

	return (
		<div className="relative flex items-center justify-between mb-4 py-2">
			{isMobile ? (
				<div onClick={onBackClick}>
					<Icon src={LeftArrow} width={20} height={20} />
				</div>
			) : (
				<Button
					type="primary"
					onClick={onBackClick}
					icon={<Icon src={LeftArrowWhite} width={'w-4'} height={'h-4'} />}
					className="absolute font-bold text-sm left-0"
				>
					{t('back')}
				</Button>
			)}
			<Text className="flex-grow text-center font-sans font-bold text-lg text-secondary-black">
				{heading}
			</Text>
		</div>
	);
};

export const LandingPage = ({
	application,
	onInterviewCandidateClick,
	onInterviewFamilyMemberClick,
	onInterviewOtherReferenceClick,
	onInterviewPreceptorClick,
	onProposerRecommendationClick,
	onCareTeamRecommendationClick,
	onZCRecommendationClick,
	onApproverRecommendationClick,
	onReviewAnswersClick,
	onViewAnswersClick,
	onAdditionalDetailsClick,
	onUnableToProceedClick,
	onBackClick,
}: LandingPageProps) => {
	const { t } = useTranslation();
	const { getTranslatedValue } = useApplicationStatusTranslation();
	const { role } = useAuth();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;

	const isAllInterviewsDone =
		application?.applicantAnswersReviewed &&
		application?.applicantInterviewDone &&
		application?.preceptorInterviewDone &&
		application?.familyMemberInterviewDone;

	const showFinalRecommendationToProposer =
		(application?.applicationStatus === ApplicationStatus.REJECTED ||
			application?.applicationStatus === ApplicationStatus.APPROVED) &&
		role === UserRole.ZC;

	const isAdmin = role === UserRole.ADMIN;

	const isAdminOrApprover = role === UserRole.APPROVER || isAdmin;

	const isSkippedToEnd = isProposerSkippedToEnd(
		application?.proposerRecommendation
	);

	const AdditionalDetailsSection = (
		<div
			className="flex font-sans justify-center gap-1 mb-1 items-center cursor-pointer"
			onClick={onAdditionalDetailsClick}
		>
			<Text className="font-bold text-sm text-primary-blue whitespace-nowrap">
				{t('additionalDetails')}
			</Text>
			<Icon src={RightArrowCircle} width={21} height={21} />
		</div>
	);

	const CareTeamInterviewSection = (
		<div className="mt-4">
			<InterviewSection
				buttonText={isAdminOrApprover ? 'view' : 'careTeamRecommendation'}
				description="provideCareTeamRecommendation"
				title="careTeamRecommendation"
				isCompleted={!isEmpty(application?.careTeamRecommendation)}
				onButtonClick={onCareTeamRecommendationClick}
				{...(isAdminOrApprover ? { skipModal: true } : {})}
			/>
		</div>
	);

	const ZCInterviewSection = (
		<div className="mt-4">
			<InterviewSection
				buttonText={isAdminOrApprover ? 'view' : 'ZCRecommendation'}
				description="provideZCRecommendation"
				title="ZCRecommendation"
				isCompleted={!isEmpty(application?.zcRecommendation)}
				onButtonClick={onZCRecommendationClick}
				{...(isAdminOrApprover ? { skipModal: true } : {})}
			/>
		</div>
	);

	const ViewApplicationSection = (
		<div className="mt-4">
			<InterviewSection
				buttonText={isAdminOrApprover ? 'view' : 'viewApplication'}
				description="viewTheAnswersProvidedByCandidateWhileApplying"
				title="candidateApplication"
				isCompleted={true}
				onButtonClick={onViewAnswersClick}
				skipModal
			/>
		</div>
	);

	const ReferenceInterviewSection = (
		<>
			<div className="mt-4">
				<InterviewSection
					buttonText={isAdminOrApprover ? 'view' : 'reviewApplication'}
					description="reviewTheAnswersProvidedByCandidateWhileApplying"
					title="candidateApplicationReview"
					isCompleted={application?.applicantAnswersReviewed}
					onButtonClick={onReviewAnswersClick}
					skipModal
				/>
			</div>
			<div className="mt-4">
				<InterviewSection
					buttonText={isAdminOrApprover ? 'view' : 'interviewCandidate'}
					description="talkToCandidateAndInterview"
					title="candidateInterview"
					isCompleted={application?.applicantInterviewDone}
					onButtonClick={onInterviewCandidateClick}
					{...(isAdminOrApprover ? { skipModal: true } : {})}
				/>
			</div>
			{application?.preceptorReference && (
				<div className="mt-4">
					<InterviewSection
						buttonText={isAdminOrApprover ? 'view' : 'interviewPreceptor'}
						description="talkToCandidatePreceptor"
						title="candidatePreceptorInterview"
						isCompleted={application?.preceptorInterviewDone}
						onButtonClick={onInterviewPreceptorClick}
						{...(isAdminOrApprover ? { skipModal: true } : {})}
					/>
				</div>
			)}
			{application?.familyMemberReference && (
				<div className="mt-4">
					<InterviewSection
						buttonText={isAdminOrApprover ? 'view' : 'interviewFamilyMember'}
						description="talkToCandidateFamilyMember"
						title="familyMemberInterview"
						isCompleted={application?.familyMemberInterviewDone}
						onButtonClick={onInterviewFamilyMemberClick}
						{...(isAdminOrApprover ? { skipModal: true } : {})}
					/>
				</div>
			)}
			{application?.otherReferenceProvided && (
				<div className="mt-4">
					<InterviewSection
						buttonText={isAdminOrApprover ? 'view' : 'interviewOtherReference'}
						description="talkToOtherReference"
						title="OtherReferenceInterview"
						isCompleted={application?.otherReferenceInterviewDone}
						onButtonClick={onInterviewOtherReferenceClick}
						{...(isAdminOrApprover ? { skipModal: true } : {})}
					/>
				</div>
			)}
		</>
	);

	const ProposerRecommendationSection = (
		<div className="mt-4">
			<InterviewSection
				buttonText={isAdminOrApprover ? 'view' : 'proposerRecommendation'}
				description="provideProposerRecommendation"
				title="proposerRecommendation"
				isCompleted={!isEmpty(application?.proposerRecommendation)}
				onButtonClick={onProposerRecommendationClick}
				{...(isAdminOrApprover ? { skipModal: true } : {})}
			/>
		</div>
	);

	const ProposerSection = (
		<>
			<div className="sm:hidden mt-6">{AdditionalDetailsSection}</div>
			{ReferenceInterviewSection}
			{isAllInterviewsDone && <>{ProposerRecommendationSection}</>}
		</>
	);

	const ApproverSection = (
		<>
			{!isEmpty(application?.proposerRecommendation) && (
				<div className="mt-4">
					<InterviewSection
						buttonText={
							isAdmin
								? 'view'
								: isAdminOrApprover &&
								  !isEmpty(application?.approverRecommendation)
								? 'view'
								: 'regionalCommitteeDecision'
						}
						description="provideRegionalCommitteeDecision"
						title="regionalCommitteeDecision"
						isCompleted={!isEmpty(application?.approverRecommendation)}
						onButtonClick={onApproverRecommendationClick}
						{...(isAdminOrApprover &&
						!isEmpty(application?.approverRecommendation)
							? { skipModal: true }
							: {})}
					/>
				</div>
			)}
		</>
	);

	const ZCSection = showFinalRecommendationToProposer ? (
		<>
			{(application?.shouldCareTeamReview ||
				!isEmpty(application?.careTeamRecommendation)) && (
				<div className="mt-4">
					<InterviewSection
						buttonText="careTeamRecommendation"
						description="provideCareTeamRecommendation"
						title="careTeamRecommendation"
						isCompleted={!isEmpty(application?.careTeamRecommendation)}
						onButtonClick={onCareTeamRecommendationClick}
					/>
				</div>
			)}
			<div className="mt-4">
				<InterviewSection
					buttonText="regionalCommitteeDecision"
					description="provideRegionalCommitteeDecision"
					title="regionalCommitteeDecision"
					isCompleted={!isEmpty(application?.approverRecommendation)}
					onButtonClick={onApproverRecommendationClick}
				/>
			</div>
		</>
	) : (
		<>
			<div className="sm:hidden mt-6">{AdditionalDetailsSection}</div>
			{ViewApplicationSection}
			{ZCInterviewSection}
		</>
	);

	const CareTeamSection = (
		<>
			<div className="sm:hidden mt-6">{AdditionalDetailsSection}</div>
			{ViewApplicationSection}
			{CareTeamInterviewSection}
		</>
	);

	const skippedApproverSection = (
		<>
			<div className="sm:hidden mt-6">{AdditionalDetailsSection}</div>
			{isSkippedToEnd ? (
				<>
					<div
						className={`flex gap-20 justify-center items-center rounded-md py-4 px-8 border border-solid border-[#E6E6E6] ${
							isMobile ? '' : 'w-[736px]'
						}`}
					>
						<Text className="font-bold text-sm text-gray-650">
							{t('proposerHasSkippedTheInterview')}
						</Text>
					</div>
					{ViewApplicationSection}
				</>
			) : (
				ReferenceInterviewSection
			)}
			{ApproverSection}
		</>
	);

	const unSkippedApproverSection = (
		<>
			<div className="sm:hidden mt-6">{AdditionalDetailsSection}</div>
			{ReferenceInterviewSection}
			{ProposerRecommendationSection}
			{ZCInterviewSection}
			{CareTeamInterviewSection}
			{ApproverSection}
		</>
	);

	let Body;
	if (role === UserRole.CARE_TEAM) {
		Body = CareTeamSection;
	} else if (role === UserRole.ZC) {
		Body = ZCSection;
	} else if (role === UserRole.ADMIN || role === UserRole.APPROVER) {
		Body = isSkippedToEnd ? skippedApproverSection : unSkippedApproverSection;
	} else {
		Body = ProposerSection;
	}

	const showSkipToEnd =
		(role === UserRole.FUNCTIONARY ||
			role === UserRole.ADMIN ||
			role === UserRole.BATCH_ADMIN) &&
		!application?.isApprover &&
		application?.applicationStatus === ApplicationStatus.FUNCTIONARY_ASSIGNED;

	return (
		<div className="flex justify-center">
			<div
				className={`flex flex-col h-full min-h-0 p-[8px] ${
					isMobile ? 'w-full' : 'w-[90%]'
				}`}
			>
				<SectionHeader
					heading={t('evaluate')}
					onBackClick={onBackClick}
					isMobile={isMobile}
				/>
				<div className={`${isMobile ? 'text-center' : 'flex justify-between'}`}>
					<div className="my-2 font-sans font-semibold text-[#595959]">
						{getTranslatedValue(application?.applicationStatus)}
					</div>
					{!isMobile && AdditionalDetailsSection}
				</div>

				<ApplicantDetailsTable applicantInfo={application.applicant} />
				<div className={`${isMobile ? '' : 'justify-items-center'}`}>
					<div
						className={`${
							isMobile
								? ''
								: 'bg-white w-fit mt-8 rounded-md px-12 pt-8 pb-4 flex flex-col items-center border border-solid border-[#E6E6E6]'
						}`}
					>
						{Body}
						{showSkipToEnd && (
							<div className="mt-[16px] flex justify-center">
								<Button
									className="text-primary-blue font-bold text-sm"
									type="link"
									onClick={onUnableToProceedClick}
								>
									{t('unableToProceed')}
								</Button>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export const LandingPageContainer = () => {
	const { application, navigateToPage } = useInterviewLandingPage();
	const { navigateWithBatchId } = useBatchId();

	const handleInterviewCandidateClick = useCallback(
		() => navigateToPage(APPLICANT_CONTEXT),
		[navigateToPage]
	);
	const handleInterviewPreceptorClick = useCallback(
		() => navigateToPage(PRECEPTOR_CONTEXT),
		[navigateToPage]
	);
	const handleInterviewFamilyMemberClick = useCallback(
		() => navigateToPage(FAMILY_CONTEXT),
		[navigateToPage]
	);
	const handleInterviewOtherReferenceClick = useCallback(
		() => navigateToPage(OTHER_REFERENCE_CONTEXT),
		[navigateToPage]
	);
	const handleProposerRecommendationClick = useCallback(
		() => navigateToPage(PROPOSER_RECOMMENDATION),
		[navigateToPage]
	);
	const handleCareTeamRecommendationClick = useCallback(
		() => navigateToPage(CARE_TEAM_RECOMMENDATION),
		[navigateToPage]
	);
	const handleZCRecommendationClick = useCallback(
		() => navigateToPage(ZC_RECOMMENDATION),
		[navigateToPage]
	);
	const handleApproverRecommendationClick = useCallback(
		() => navigateToPage(APPROVER_RECOMMENDATION),
		[navigateToPage]
	);
	const handleAnswersReviewClick = useCallback(
		() => navigateToPage(ANSWERS_REVIEW_CONTEXT),
		[navigateToPage]
	);
	const handleViewAnswersClick = useCallback(
		() => navigateToPage(VIEW_ANSWERS),
		[navigateToPage]
	);
	const handleAdditionalDetailsClick = useCallback(
		() => navigateToPage(APPLICANT_ADDITIONAL_DETAILS),
		[navigateToPage]
	);
	const handleUnableToProceedClick = useCallback(
		() => navigateToPage(SKIP_ALL_INTERVIEWS),
		[navigateToPage]
	);
	const handleBackClick = useCallback(
		() => navigateWithBatchId(FUNCTIONARY_HOME),
		[navigateWithBatchId]
	);

	return (
		<LandingPage
			application={application}
			onReviewAnswersClick={handleAnswersReviewClick}
			onViewAnswersClick={handleViewAnswersClick}
			onInterviewCandidateClick={handleInterviewCandidateClick}
			onInterviewPreceptorClick={handleInterviewPreceptorClick}
			onInterviewFamilyMemberClick={handleInterviewFamilyMemberClick}
			onInterviewOtherReferenceClick={handleInterviewOtherReferenceClick}
			onProposerRecommendationClick={handleProposerRecommendationClick}
			onCareTeamRecommendationClick={handleCareTeamRecommendationClick}
			onZCRecommendationClick={handleZCRecommendationClick}
			onApproverRecommendationClick={handleApproverRecommendationClick}
			onAdditionalDetailsClick={handleAdditionalDetailsClick}
			onUnableToProceedClick={handleUnableToProceedClick}
			onBackClick={handleBackClick}
		/>
	);
};
